import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { loadServiceDetailsForContractId, loadUploaderTypes } from '../../../store/directusService';
import { addWebsiteCsv, onClearDocumentErrors } from '../../../store/documentService';
import SingleSelect from '../../formElements/SingleSelect';
import Uploader from '../Uploader';
import UploaderResults from '../UploaderResults';

import modal from '../../../commonStyles/modal.module.css';

const WebsiteUploaderModal = ({ onClose, isOpen }) => {
    const dispatch = useDispatch();
    const [contractId, setContractId] = useState('');
    const [failures, setFailures] = useState([]);
    const [fileName, setFileName] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [serviceId, setServiceId] = useState('');
    const [services, setServices] = useState([]);
    const [enabledContracts, setEnabledContracts] = useState([]);

    const serviceDetailsForContractId = useSelector(
        (state) => state.entities.directusService.serviceDetailsForContractId
    );
    const uploaderTypes = useSelector((state) => state.entities.directusService.uploaderTypes);

    useEffect(() => {
        if (!uploaderTypes.length) dispatch(loadUploaderTypes());
    }, []);

    useEffect(() => {
        if (!uploaderTypes.length) return;
        const validContracts = uploaderTypes
            .find((el) => el.name.toLowerCase() === 'website uploader')
            ?.contract.map((el) => el.contract_id);

        setEnabledContracts(validContracts);
    }, [uploaderTypes]);

    useEffect(() => {
        contractId && setServices(serviceDetailsForContractId);
    }, [serviceDetailsForContractId]);

    const onLoaded = (uploadErrors, csvFileName) => {
        uploadErrors && setFailures(uploadErrors);
        csvFileName && setFileName(csvFileName.split('.')[0]);
        setIsLoaded(true);
    };

    const updateContract = (id) => {
        setContractId(id);
        setServiceId('');
        if (!id) setServices([]);
        else dispatch(loadServiceDetailsForContractId(id));
    };

    const updateService = (serviceId) => {
        setServiceId(serviceId);
    };

    const onCancel = () => {
        setContractId('');
        setFailures([]);
        setFileName('');
        setIsLoaded(false);
        onClose();
    };

    const onUpload = (payload, successMsg) => {
        dispatch(addWebsiteCsv(payload, successMsg));
    };

    const onResultsClose = () => {
        dispatch(onClearDocumentErrors());
        setFailures([]);
        setIsLoaded(false);
        onCancel();
    };

    return (
        <Dialog
            onClose={(e, reason) => reason !== 'backdropClick' && onCancel()}
            open={isOpen}
            fullWidth={true}>
            <div className={modal.modalHeader}>
                <DialogTitle>Website Uploader</DialogTitle>
            </div>
            <DialogContent>
                {!isLoaded ? (
                    <>
                        <div>
                            <SingleSelect
                                id={'contract'}
                                label={'Contract'}
                                placeholder={'Select Contract'}
                                mandatory={true}
                                menuItems={enabledContracts || []}
                                selectedId={contractId || ''}
                                selected={
                                    enabledContracts?.find((el) => el.id === contractId) || {}
                                }
                                onChange={updateContract}
                            />
                            <SingleSelect
                                id={'service'}
                                label={'Service'}
                                placeholder="Select Service..."
                                disabled={!contractId}
                                mandatory={true}
                                menuItems={services}
                                selectedId={serviceId}
                                selected={services.find((el) => el.id === serviceId) || {}}
                                onChange={updateService}
                            />
                        </div>
                        <Uploader
                            uploadData={[
                                { key: 'contractId', value: contractId },
                                { key: 'serviceId', value: serviceId }
                            ]}
                            acceptedFileTypes={['text/csv']}
                            fileTypes={'.csv'}
                            maxFileSize={25}
                            onUpload={onUpload}
                            onClose={onLoaded}
                            onCancel={onCancel}
                            isDisabled={!serviceId}
                        />
                    </>
                ) : (
                    <UploaderResults
                        fileName={fileName}
                        failures={failures}
                        onClose={onResultsClose}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default WebsiteUploaderModal;

WebsiteUploaderModal.propTypes = {
    onClose: PropTypes.func,
    isOpen: PropTypes.bool
};
